import { Box, CircularProgress, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { APPROACHING_WINDOW_DAYS } from "encamp-waste-accumulation";
import {
  ClockStatus,
  RcraInfoFederalWasteGeneratorStatus,
  UnitType,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";

import {
  ClockProjection,
  clockPlaceholderText,
  PreviewEpisodicEvent,
  projectCentralAccumulationClock,
  projectEpisodicClock,
} from "./containerClockPreview";
import { approachingNote, containerFillFraction, formatAmountCapacity } from "./containerDisplay";
import { SectionCard } from "./containerSections";

const DIAL_SIZE = 148;

function ClockPreviewDial({ projection }: { projection: ClockProjection }) {
  const theme = useTheme();
  const used = Math.min(projection.totalDays, projection.totalDays - projection.daysRemaining);
  const fraction = Math.max(0, Math.min(1, used / projection.totalDays));
  const overdue = projection.daysRemaining < 0;
  // Only lateness is colored here too — see the container drawer's dial.
  const ringColor = overdue ? theme.palette.error.main : theme.palette.text.primary;
  const note = approachingNote(
    projection.daysRemaining <= APPROACHING_WINDOW_DAYS ? ClockStatus.Approaching : ClockStatus.Ok,
    overdue,
    projection.basis
  );

  return (
    <Stack spacing={2} alignItems="center">
      <Box sx={{ position: "relative", width: DIAL_SIZE, height: DIAL_SIZE }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={DIAL_SIZE}
          thickness={4}
          sx={{ color: theme.palette.divider, position: "absolute", inset: 0 }}
        />
        <CircularProgress
          variant="determinate"
          value={fraction * 100}
          size={DIAL_SIZE}
          thickness={4}
          sx={{ color: ringColor, position: "absolute", inset: 0 }}
        />
        <Tooltip title={note ?? ""}>
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ position: "absolute", inset: 0, textAlign: "center" }}
          >
            <Typography variant="h5" sx={{ lineHeight: 1 }}>
              {Math.abs(projection.daysRemaining)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {overdue ? "days overdue" : "days left"}
            </Typography>
          </Stack>
        </Tooltip>
      </Box>
      <Stack spacing={1} sx={{ width: "100%" }}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Deadline
          </Typography>
          <Typography variant="body2">{projection.deadline.toFormat("LLLL d, yyyy")}</Typography>
        </Box>
        <StatusChip label={projection.basis} color="default" variant="soft" />
      </Stack>
    </Stack>
  );
}

function FillPreview({
  amount,
  amountUnit,
  capacity,
  capacityUnit,
}: {
  amount: number | null;
  amountUnit: UnitType | null;
  capacity: number | null;
  capacityUnit: UnitType | null;
}) {
  const fraction = containerFillFraction({ amount, amountUnit, capacity, capacityUnit });

  return (
    <Stack direction="row" spacing={3} alignItems="center">
      <Box
        sx={{
          position: "relative",
          width: 64,
          height: 120,
          borderRadius: 2,
          bgcolor: "divider",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {fraction !== null && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: `${fraction * 100}%`,
              bgcolor: "rgba(15, 95, 107, 0.5)",
              borderTop: fraction > 0 ? "1px solid #fff" : "none",
              "@media (prefers-reduced-motion: no-preference)": {
                transition: "height 0.3s ease-in-out",
              },
            }}
          />
        )}
      </Box>
      <Stack spacing={1}>
        <Typography variant="body2">
          {formatAmountCapacity({ amount, amountUnit, capacity, capacityUnit })}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {amount != null ? "Recorded as the first fill event" : "No amount recorded yet"}
        </Typography>
      </Stack>
    </Stack>
  );
}

function EpisodicClockCard({ event }: { event: PreviewEpisodicEvent }) {
  const projection = projectEpisodicClock({
    startDate: event.startDate,
    accumulationDeadline: event.accumulationDeadline,
  });

  return (
    <SectionCard title="Episodic event clock">
      <Stack spacing={1.5}>
        {/* The event's start, stated because it is what the countdown runs
            from — not the accumulation start date the form collects above. */}
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Event start
          </Typography>
          <Typography variant="body2">
            {DateTime.fromISO(event.startDate).toFormat("LLLL d, yyyy")}
          </Typography>
        </Box>
        {projection && <ClockPreviewDial projection={projection} />}
      </Stack>
    </SectionCard>
  );
}

export function ContainerFormPreview({
  generatorStatus,
  movedToCentralDate,
  sqgOver200Miles,
  amount,
  amountUnit,
  capacity,
  capacityUnit,
  episodicEvent,
}: {
  generatorStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined;
  movedToCentralDate: string | null;
  sqgOver200Miles: boolean;
  amount: number | null;
  amountUnit: UnitType | null;
  capacity: number | null;
  capacityUnit: UnitType | null;
  episodicEvent?: PreviewEpisodicEvent;
}) {
  const projection = projectCentralAccumulationClock({
    generatorStatus,
    movedToCentralDate,
    sqgOver200Miles,
  });

  return (
    <Stack spacing={2}>
      {episodicEvent ? (
        <EpisodicClockCard event={episodicEvent} />
      ) : (
        <SectionCard title="Central accumulation clock">
          {projection ? (
            <ClockPreviewDial projection={projection} />
          ) : (
            <Typography variant="caption" color="text.secondary">
              {clockPlaceholderText(generatorStatus, sqgOver200Miles)}
            </Typography>
          )}
        </SectionCard>
      )}
      <SectionCard title="Fill">
        <FillPreview
          amount={amount}
          amountUnit={amountUnit}
          capacity={capacity}
          capacityUnit={capacityUnit}
        />
      </SectionCard>
    </Stack>
  );
}
